import { useState } from 'react';
import { useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { Button, Paragraph, ScrollView, Separator, Spinner, XStack, YStack } from 'tamagui';
import { Screen } from '@mrrmlab/ui';
import { useDebugParseShoppingListImage, type OcrModelId } from '@mrrmlab/feature-shopping-list';
import { ocrImageStore } from '../ocr-image-store';
import type { DebugParseImageResponse } from '@mrrmlab/api-client';

export function ListImageDebugRoute() {
  const { listId } = useParams({ from: '/lists/$listId/image-debug' });
  const { model } = useSearch({ from: '/lists/$listId/image-debug' });
  const navigate = useNavigate();
  const debugParse = useDebugParseShoppingListImage();

  const [file] = useState<File | null>(() => ocrImageStore.consume());
  const [result, setResult] = useState<DebugParseImageResponse | null>(null);

  const back = () => void navigate({ to: '/lists/$listId', params: { listId } });

  const handleAnalyse = () => {
    if (!file) return;
    debugParse.mutate({ file, model: model as OcrModelId }, { onSuccess: setResult });
  };

  return (
    <Screen>
      <XStack justifyContent="space-between" alignItems="center">
        <Button size="$2" chromeless onPress={back} disabled={debugParse.isPending}>
          ← Zurück
        </Button>
        <Paragraph fontWeight="600">OCR Debug</Paragraph>
        <Button
          size="$3"
          theme="active"
          disabled={debugParse.isPending || !file}
          onPress={handleAnalyse}
        >
          {debugParse.isPending ? <Spinner size="small" /> : 'Analysieren'}
        </Button>
      </XStack>

      {debugParse.isError && (
        <Paragraph color="$red10" fontSize="$3">
          Fehler: {debugParse.error.message}
        </Paragraph>
      )}

      {!result && !debugParse.isPending && (
        <YStack flex={1} justifyContent="center" alignItems="center">
          <Paragraph opacity={0.5}>
            {file ? 'Bild geladen — „Analysieren" drücken.' : 'Kein Bild vorhanden.'}
          </Paragraph>
        </YStack>
      )}

      {result && (
        <ScrollView flex={1}>
          <YStack gap="$4" paddingVertical="$3">

            <XStack gap="$3" flexWrap="wrap">
              <YStack flex={1} minWidth={140} gap="$1">
                <Paragraph fontWeight="600" fontSize="$3">Original (nach EXIF)</Paragraph>
                <img
                  src={`data:image/jpeg;base64,${result.originalImage}`}
                  alt="Original"
                  style={{ width: '100%', borderRadius: 8, border: '1px solid #ccc' }}
                />
              </YStack>
              <YStack flex={1} minWidth={140} gap="$1">
                <Paragraph fontWeight="600" fontSize="$3">Nach Vorverarbeitung</Paragraph>
                <img
                  src={`data:image/jpeg;base64,${result.preprocessedImage}`}
                  alt="Vorverarbeitet"
                  style={{ width: '100%', borderRadius: 8, border: '1px solid #ccc' }}
                />
              </YStack>
            </XStack>

            <Separator />

            <Paragraph fontWeight="700" fontSize="$4">
              Erkannte Zeilen ({result.lines.length})
            </Paragraph>

            {result.lines.length === 0 && (
              <Paragraph opacity={0.5} fontStyle="italic">Keine Zeilen erkannt.</Paragraph>
            )}

            {result.lines.map((line, i) => (
              <XStack key={i} gap="$3" alignItems="center">
                {line.crop ? (
                  <img
                    src={`data:image/jpeg;base64,${line.crop}`}
                    alt={`Zeile ${i + 1}`}
                    style={{
                      height: 40,
                      maxWidth: 180,
                      objectFit: 'contain',
                      borderRadius: 4,
                      border: '1px solid #ccc',
                      flexShrink: 0,
                    }}
                  />
                ) : null}
                <YStack flex={1}>
                  <Paragraph fontSize="$4">{line.text || '—'}</Paragraph>
                  <Paragraph fontSize="$2" opacity={0.5}>
                    Konfidenz: {Math.round(line.confidence * 100)} %
                  </Paragraph>
                </YStack>
              </XStack>
            ))}
          </YStack>
        </ScrollView>
      )}
    </Screen>
  );
}
